import axios from "axios";
import { BatchOptions, JobStatus, OutputFile, SshFile, SshRunOptions } from "../types";
import { authHeader, hasAuth } from "./auth.header";

/* -------------------------------------------------------------------------------- *\
|                               auth interceptor
\* -------------------------------------------------------------------------------- */
// If any API call comes back 401/403 (expired/invalid JWT), drop the session and
// send the user back to login. Ignore the signin call itself.
axios.interceptors.response.use(
    (response) => response,
    (error) =>
    {
        const status = error?.response?.status;
        const url: string = error?.config?.url || '';
        if ((status === 401 || status === 403) && !url.includes('/api/signin'))
        {
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login')
            {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);
/* -------------------------------------------------------------------------------- *\
|                               findapiserverurl
\* -------------------------------------------------------------------------------- */
export const findapiserverurl = (): string =>
{
    if (process.env.REACT_APP__API_URL) return process.env.REACT_APP__API_URL;

    // In dev the front runs on :3000/:3001, the API on :5024.
    const origin: string = window.location.origin;
    return origin.replace('3000', '5024').replace('3001', '5024');
}
/* -------------------------------------------------------------------------------- *\
|                               batch_start
\* -------------------------------------------------------------------------------- */
/** Upload .tar.gz archives + options as multipart/form-data. */
export const batch_start = async (files: File[], opts: BatchOptions): Promise<{ jobId: string, files: string[] }> =>
{
    if (!hasAuth()) return Promise.reject(new Error("not authenticated"));

    const form = new FormData();
    files.forEach(f => form.append("files", f, f.name));
    if (opts.startDate)  form.append("startDate", opts.startDate);
    if (opts.endDate)    form.append("endDate", opts.endDate);
    if (opts.deploy)     form.append("deploy", opts.deploy);
    if (opts.cacheDir)   form.append("cacheDir", opts.cacheDir);
    if (opts.useCache)   form.append("useCache", "true");
    if (opts.verbose)    form.append("verbose", "true");

    const response = await axios.post(findapiserverurl() + "/api/batch/start", form, { headers: authHeader() });
    return response.data;
}
/* -------------------------------------------------------------------------------- *\
|                               batch_status
\* -------------------------------------------------------------------------------- */
export const batch_status = async (jobId: string, from: number): Promise<JobStatus> =>
{
    const response = await axios.get(
        findapiserverurl() + `/api/batch/status/${jobId}`,
        { params: { from }, headers: authHeader() }
    );
    return response.data as JobStatus;
}
/* -------------------------------------------------------------------------------- *\
|                               batch_stop
\* -------------------------------------------------------------------------------- */
export const batch_stop = async (jobId: string): Promise<void> =>
{
    await axios.post(findapiserverurl() + `/api/batch/stop/${jobId}`, {}, { headers: authHeader() });
}
/* -------------------------------------------------------------------------------- *\
|                               batch_outputs
\* -------------------------------------------------------------------------------- */
export const batch_outputs = async (jobId: string): Promise<OutputFile[]> =>
{
    const response = await axios.get(
        findapiserverurl() + `/api/batch/outputs/${jobId}`,
        { headers: authHeader() }
    );
    return (response.data?.files ?? []) as OutputFile[];
}
/* -------------------------------------------------------------------------------- *\
|                               batch_download
\* -------------------------------------------------------------------------------- */
/** Fetch a result file as a blob (with auth) and trigger a browser download. */
export const batch_download = async (jobId: string, name: string): Promise<void> =>
{
    const response = await axios.get(
        findapiserverurl() + `/api/batch/download/${jobId}/${encodeURIComponent(name)}`,
        { headers: authHeader(), responseType: 'blob' }
    );
    triggerDownload(new Blob([response.data]), name);
}
/* -------------------------------------------------------------------------------- *\
|                               triggerDownload
\* -------------------------------------------------------------------------------- */
function triggerDownload(blob: Blob, filename: string): void
{
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_list_years
\* -------------------------------------------------------------------------------- */
export const ssh_list_years = async (): Promise<{ years: string[], access: string[] }> =>
{
    const response = await axios.post(findapiserverurl() + "/api/ssh/list", {}, { headers: authHeader() });
    return { years: response.data?.years ?? [], access: response.data?.access ?? [] };
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_list_files
\* -------------------------------------------------------------------------------- */
export const ssh_list_files = async (year: string): Promise<{ files: SshFile[], access: string[] }> =>
{
    const response = await axios.post(findapiserverurl() + "/api/ssh/list", { year }, { headers: authHeader() });
    return { files: response.data?.files ?? [], access: response.data?.access ?? [] };
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_start
\* -------------------------------------------------------------------------------- */
export const ssh_start = async (paths: string[], opts: SshRunOptions): Promise<{ jobId: string }> =>
{
    const body = { paths, ...opts };
    const response = await axios.post(findapiserverurl() + "/api/ssh/start", body, { headers: authHeader() });
    return response.data;
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_current
\* -------------------------------------------------------------------------------- */
export interface SshCurrent
{
    jobId: string;
    running: boolean;
    exitCode: number | null;
    startedAt: number;
    endedAt: number | null;
    total: number;
    files: string[];
    paths: string[];
    relaunchMec: boolean;
}
/** The single global SSH job, or null when none exists — used to rejoin on reconnect. */
export const ssh_current = async (): Promise<SshCurrent | null> =>
{
    const response = await axios.get(findapiserverurl() + "/api/ssh/current", { headers: authHeader() });
    return response.data?.jobId ? (response.data as SshCurrent) : null;
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_status / ssh_stop
\* -------------------------------------------------------------------------------- */
export const ssh_status = async (jobId: string, from: number): Promise<JobStatus> =>
{
    const response = await axios.get(
        findapiserverurl() + `/api/ssh/status/${jobId}`,
        { params: { from }, headers: authHeader() }
    );
    return response.data as JobStatus;
}

export const ssh_stop = async (jobId: string): Promise<void> =>
{
    await axios.post(findapiserverurl() + `/api/ssh/stop/${jobId}`, {}, { headers: authHeader() });
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_concurrency
\* -------------------------------------------------------------------------------- */
export const ssh_concurrency = async (jobId: string, n: number): Promise<void> =>
{
    await axios.post(findapiserverurl() + `/api/ssh/concurrency/${jobId}`, { n }, { headers: authHeader() });
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_outputs
\* -------------------------------------------------------------------------------- */
export const ssh_outputs = async (jobId: string): Promise<OutputFile[]> =>
{
    const response = await axios.get(findapiserverurl() + `/api/ssh/outputs/${jobId}`, { headers: authHeader() });
    return (response.data?.files ?? []) as OutputFile[];
}
/* -------------------------------------------------------------------------------- *\
|                               ssh_download_zip
\* -------------------------------------------------------------------------------- */
export const ssh_download_zip = async (jobId: string): Promise<void> =>
{
    const response = await axios.get(
        findapiserverurl() + `/api/ssh/zip/${jobId}`,
        { headers: authHeader(), responseType: 'blob' }
    );
    triggerDownload(new Blob([response.data]), `results_${jobId}.zip`);
}
